import { useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  Input,
} from '@eureca/ui';
import type { AssessmentListEntry } from './assessments-query';

export interface AssessmentPolicyValues {
  inheritable: boolean;
  inheritTtlMonths: number | null;
}

export interface AssessmentPolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The PRESET being edited — ad-hoc rows never open this dialog. */
  assessment: AssessmentListEntry | null;
  isSaving: boolean;
  onSave: (values: AssessmentPolicyValues) => void;
}

/**
 * Validade em meses como o usuário digita (string). Vazio = prazo PADRÃO do
 * Core (`inheritTtlMonths: null` → 6 meses, EU-750 — "sem prazo" não existe
 * mais); preenchido precisa ser inteiro ≥ 1 — espelha o
 * `z.number().int().min(1).nullable()` do contrato do Core (spec 003).
 */
const MonthsInputSchema = z
  .string()
  .refine((v) => v.trim() === '' || (/^\d+$/.test(v.trim()) && Number(v.trim()) >= 1), {
    message: 'Informe um número inteiro de meses (mínimo 1) ou deixe vazio.',
  });

/**
 * Edição da política de reuso cross-vaga de um teste PADRÃO (preset):
 * apenas `{inheritable, inheritTtlMonths}` — Core rejeita qualquer outro
 * campo em presets (409 `preset_immutable`).
 */
export function AssessmentPolicyDialog({
  open,
  onOpenChange,
  assessment,
  isSaving,
  onSave,
}: AssessmentPolicyDialogProps) {
  const [inheritable, setInheritable] = useState(false);
  const [months, setMonths] = useState('');
  const [monthsError, setMonthsError] = useState<string | undefined>(undefined);

  // Re-seed the form from the row whenever a (new) assessment opens the dialog.
  useEffect(() => {
    if (open && assessment) {
      setInheritable(assessment.inheritable);
      setMonths(assessment.inheritTtlMonths !== null ? String(assessment.inheritTtlMonths) : '');
      setMonthsError(undefined);
    }
  }, [open, assessment]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = MonthsInputSchema.safeParse(months);
    if (inheritable && !parsed.success) {
      setMonthsError(parsed.error.issues[0]?.message);
      return;
    }
    setMonthsError(undefined);
    const trimmed = months.trim();
    onSave({
      inheritable,
      // Desligado ou vazio = null. Com reuso ligado, null = prazo PADRÃO de 6
      // meses no Core (EU-750); desligado, o TTL é irrelevante.
      inheritTtlMonths: inheritable && trimmed !== '' ? Number(trimmed) : null,
    });
  }

  function handleInheritableChange(checked: boolean) {
    setInheritable(checked);
    if (!checked) setMonthsError(undefined);
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!isSaving ? onOpenChange(next) : undefined)}>
      <DialogContent className="gap-0 p-0 lg:max-w-[480px]">
        <DialogHeader className="gap-1.5 px-6 pt-6">
          <DialogTitle className="text-lg font-semibold leading-none tracking-[-0.025em] text-foreground">
            Política de reuso
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {assessment ? assessment.name : ''}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-5 px-6 pt-4 pb-8">
            <FormField>
              <label className="flex cursor-pointer flex-row items-center gap-2 text-sm text-foreground">
                <Checkbox
                  checked={inheritable}
                  onCheckedChange={(checked) => handleInheritableChange(checked === true)}
                  data-testid="assessment-policy-inheritable"
                />
                Reaproveitar entre vagas
              </label>
              <FormFieldHint>
                Quando ligado, o resultado do candidato neste teste vale para outras vagas dentro da
                validade.
              </FormFieldHint>
            </FormField>

            <FormField error={Boolean(monthsError)}>
              <FormFieldLabel className="text-foreground">
                Validade do resultado (meses)
              </FormFieldLabel>
              <Input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={inheritable ? months : ''}
                placeholder={inheritable ? 'Vazio = padrão (6 meses)' : 'Não aplicável'}
                disabled={!inheritable}
                variant={monthsError ? 'destructive' : 'default'}
                onChange={(e) => setMonths(e.target.value)}
                data-testid="assessment-policy-ttl-months"
              />
              <FormFieldError className="text-sm leading-5 text-muted-foreground">
                {monthsError}
              </FormFieldError>
            </FormField>
          </div>
          <DialogFooter className="px-6 pb-6">
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={isSaving}>
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" loading={isSaving} data-testid="assessment-policy-save">
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
